/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  Button,
  Box,
  InputAdornment,
  LinearProgress,
  MenuItem,
} from '@mui/material';
import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  defaultFormOptions,
  ReactHookFormProvider,
  ReactHookFormSelect,
  ReactHookFormTextField,
} from '@opengeoweb/form-fields';
import type { ServiceInterface, TimeSeriesService } from '@opengeoweb/shared';
import { CustomDialog, CustomIconButton } from '@opengeoweb/shared';
import { Copy } from '@opengeoweb/theme';
import type { ValidationResult } from './utils';
import { validateServiceName, validateServiceUrl } from './utils';
import type { TimeSeriesTypes } from '../../../store';
import { TIME_SERIES_NAMESPACE } from '../../../utils/i18n';
import {
  useGetCollections,
  useGetServiceInformation,
} from '../../../utils/timeseries-api/hooks';

const style = {
  textField: {
    width: '100%',
    marginBottom: 2,
  },
};

export interface ServicePopupProps {
  servicePopupVariant: TimeSeriesTypes.PopupVariant;
  hideBackdrop?: boolean;
  isOpen?: boolean;
  closePopup: () => void;
  shouldDisableAutoFocus?: boolean;
  serviceUrl?: string;
  serviceName?: string;
  serviceDescription?: string;
  serviceType?: ServiceInterface;
  services?: TimeSeriesService[];
  serviceSetService: (payload: TimeSeriesService) => void;
  showSnackbar?: (message: string) => void;
}

const getTranslationIdForVariant = (
  variant: TimeSeriesTypes.PopupVariant,
): string => {
  switch (variant) {
    case 'add':
      return 'timeseries-add-service';
    case 'edit':
      return 'timeseries-edit-service';
    case 'save':
      return 'timeseries-save-service';
    default:
      return 'timeseries-service';
  }
};

const fields = {
  serviceUrl: {
    name: 'serviceUrl',
    labelTranslationId: 'timeseries-service-refers-to-url',
  },
  serviceName: {
    name: 'serviceName',
    labelTranslationId: 'timeseries-service-name',
  },
  serviceDescription: {
    name: 'serviceDescription',
    labelTranslationId: 'timeseries-description',
  },
  serviceType: {
    name: 'serviceType',
    labelTranslationId: 'timeseries-type',
  },
};

interface InitialValidationsTriggerProps {
  trigger: (name?: string | string[]) => Promise<boolean>;
  getValues: (name?: string | string[]) => string;
  onValidField: (value: string) => void;
}

const InitialValidationsTrigger: React.FC<InitialValidationsTriggerProps> = ({
  trigger,
  getValues,
  onValidField,
}: InitialValidationsTriggerProps) => {
  React.useEffect(() => {
    const value = getValues(fields.serviceUrl.name);
    if (value) {
      void trigger(fields.serviceUrl.name).then(
        (isValid) => isValid && onValidField(value),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
};
interface FormValues {
  serviceUrl: string;
  serviceName: string;
  serviceDescription: string;
  serviceType: ServiceInterface;
}

const ServicePopupUnwrapped: React.FC<ServicePopupProps> = ({
  servicePopupVariant,
  hideBackdrop = false,
  isOpen,
  closePopup,
  shouldDisableAutoFocus,
  services,
  serviceSetService,
  serviceUrl,
  serviceName,
  serviceDescription,
  serviceType,
  showSnackbar = (): void => {},
}: ServicePopupProps) => {
  const {
    formState: { errors },
    handleSubmit,
    setError,
    clearErrors,
    watch,
    trigger,
    getValues,
    setValue,
  } = useFormContext();
  const { t } = useTranslation(TIME_SERIES_NAMESPACE);

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [blurredUrl, setBlurredUrl] = React.useState<string>('');
  const {
    data: serviceData,
    isFetching: isFetchingService,
    isError: hasErrorService,
  } = useGetServiceInformation(blurredUrl);
  const {
    data: collectionsData,
    isFetching: isFetchingCollections,
    isError: hasErrorCollections,
  } = useGetCollections(blurredUrl);

  useEffect(() => {
    if (collectionsData && collectionsData.length > 0 && serviceData) {
      const currentValues = getValues();
      if (!currentValues[fields.serviceName.name]) {
        setValue(fields.serviceName.name, serviceData.title, {
          shouldValidate: true,
        });
      }
      if (!currentValues[fields.serviceDescription.name]) {
        setValue(fields.serviceDescription.name, serviceData.description, {
          shouldValidate: true,
        });
      }
      if (!currentValues[fields.serviceType.name]) {
        setValue(fields.serviceType.name, 'EDR', { shouldValidate: true });
      }
    }
  }, [collectionsData, getValues, serviceData, setError, setValue, t]);

  useEffect(() => {
    if (isFetchingService || isFetchingCollections) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [isFetchingCollections, isFetchingService]);

  useEffect(() => {
    if (hasErrorCollections || hasErrorService) {
      setError(fields.serviceUrl.name, {
        message: t('timeseries-validations-request-failed'),
        type: 'SYSTEM',
      });
    } else {
      clearErrors(fields.serviceUrl.name);
    }
  }, [clearErrors, hasErrorCollections, hasErrorService, setError, t]);

  const addNewService = async (formValues: FormValues): Promise<void> => {
    const service = services?.find((el) => el.url === serviceUrl);

    serviceSetService({
      id: service?.id || '',
      name: formValues.serviceName,
      url: formValues.serviceUrl,
      description: formValues.serviceDescription,
      type: formValues.serviceType,
      scope: 'user',
    });
    showSnackbar(
      t('timeseries-service-saved-successfully', {
        service: formValues.serviceName,
      }),
    );
    closePopup();
  };

  const onValidateServiceUrl = (value: string): ValidationResult => {
    const res = validateServiceUrl(value, services!);
    if (typeof res === 'string') {
      return t(res);
    }
    return res;
  };

  const onFocusServiceUrl = (): void => {
    const hasValue = getValues(fields.serviceUrl.name);
    if (!errors[fields.serviceUrl.name] && hasValue) {
      void trigger(fields.serviceUrl.name);
    }
  };

  const onBlurServiceUrl = (): void => {
    void trigger(fields.serviceUrl.name).then(() => {
      const value = getValues(fields.serviceUrl.name);
      if (!errors[fields.serviceUrl.name] && value) {
        setBlurredUrl(value);
      }
    });
  };

  const onKeyDownServiceUrl = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ): void => {
    if (event.key === 'Enter') {
      onBlurServiceUrl();
    }
  };

  const onValidateServiceName = (value: string): ValidationResult => {
    const res = validateServiceName(value, services!, serviceName);
    if (typeof res === 'string') {
      return t(res);
    }
    return res;
  };

  const getFormAreRequiredFieldsEmpty = (): boolean => {
    const serviceNameField = watch(fields.serviceName.name);
    const serviceUrlField = watch(fields.serviceUrl.name);

    return (
      serviceNameField === '' ||
      serviceNameField === undefined ||
      serviceUrlField === '' ||
      serviceUrlField === undefined
    );
  };

  const getFormHasformErrors = (): boolean => {
    const totalErrors = Object.keys(errors).length;
    if (totalErrors > 0) {
      // prevent SYSTEM error types from blocking the save button
      if (totalErrors === 1 && errors.serviceUrl) {
        return (
          errors.serviceUrl.type !== undefined &&
          errors.serviceUrl.type !== 'SYSTEM'
        );
      }
      return true;
    }
    return false;
  };

  const isSubmitDisabled =
    getFormHasformErrors() || getFormAreRequiredFieldsEmpty();

  const isShowPopup = servicePopupVariant === 'show';
  const isServiceUrlDisabled = isShowPopup || servicePopupVariant === 'edit';
  //
  return (
    <CustomDialog
      actions={
        isShowPopup ? (
          <Button
            onClick={closePopup}
            sx={{ width: '117px' }}
            variant="tertiary"
          >
            {t('timeseries-close')}
          </Button>
        ) : (
          <>
            <Button
              data-testid="closePopupButtonCancel"
              onClick={closePopup}
              sx={{ width: '117px' }}
              variant="tertiary"
            >
              {t('timeseries-cancel')}
            </Button>
            <Button
              data-testid="saveServiceButton"
              disabled={isSubmitDisabled || isLoading}
              form="timeseriesServicePopupForm"
              sx={{ width: '117px', marginLeft: '16px!important' }}
              type="submit"
              variant="primary"
            >
              {t('timeseries-save')}
            </Button>
          </>
        )
      }
      data-testid="servicePopup"
      disableAutoFocus
      fullWidth
      hideBackdrop={hideBackdrop}
      maxWidth="xs"
      onClose={closePopup}
      open={isOpen!}
      title={t(getTranslationIdForVariant(servicePopupVariant))}
    >
      {!!isLoading && (
        <Box sx={{ position: 'relative' }}>
          <LinearProgress
            color="secondary"
            data-testid="loadingIndicator"
            sx={{ position: 'absolute', width: '100%' }}
          />
        </Box>
      )}
      <form
        id="timeseriesServicePopupForm"
        onSubmit={handleSubmit(async (formValues) =>
          addNewService(formValues as FormValues),
        )}
      >
        <ReactHookFormTextField
          autoFocus={!shouldDisableAutoFocus}
          defaultValue={serviceUrl}
          disabled={isServiceUrlDisabled || isLoading}
          inputSlotProps={{
            disableUnderline: isShowPopup,
            endAdornment: isShowPopup && (
              <InputAdornment position="end">
                <CustomIconButton
                  aria-label={t('timeseries-service-url-copy')}
                  onClick={(): void => {
                    void navigator.clipboard.writeText(serviceUrl!);
                    showSnackbar(t('timeseries-service-url-copied'));
                  }}
                  sx={{ marginTop: 0.5 }}
                >
                  <Copy />
                </CustomIconButton>
              </InputAdornment>
            ),
          }}
          isReadOnly={isShowPopup}
          label={t(fields.serviceUrl.labelTranslationId)}
          name={fields.serviceUrl.name}
          onBlur={onBlurServiceUrl}
          onFocus={onFocusServiceUrl}
          onKeyDown={onKeyDownServiceUrl}
          rules={{
            required: true,
            validate: {
              ...(!isServiceUrlDisabled && {
                onValidateServiceUrl,
              }),
            },
          }}
          sx={style.textField}
        />

        <ReactHookFormTextField
          defaultValue={serviceName}
          disabled={isShowPopup || isLoading}
          inputSlotProps={{ disableUnderline: isShowPopup }}
          isReadOnly={isShowPopup}
          label={t(fields.serviceName.labelTranslationId)}
          name={fields.serviceName.name}
          rules={{
            required: true,
            validate: {
              onValidateServiceName,
            },
          }}
          sx={style.textField}
        />

        <ReactHookFormTextField
          defaultValue={serviceDescription || ''}
          disabled={isShowPopup || isLoading}
          helperText={
            !isShowPopup ? t('timeseries-description-helper-text') : ''
          }
          inputSlotProps={{ disableUnderline: isShowPopup }}
          isReadOnly={isShowPopup}
          label={t(fields.serviceDescription.labelTranslationId)}
          multiline
          name={fields.serviceDescription.name}
          rows={4}
          sx={style.textField}
        />

        <ReactHookFormSelect
          defaultValue={serviceType || 'EDR'}
          disabled
          isReadOnly={isShowPopup}
          label={t(fields.serviceType.labelTranslationId)}
          name={fields.serviceType.name}
          rules={{
            required: true,
          }}
        >
          <MenuItem value="EDR">EDR</MenuItem>
        </ReactHookFormSelect>

        {!isServiceUrlDisabled && (
          <InitialValidationsTrigger
            getValues={getValues}
            onValidField={onBlurServiceUrl}
            trigger={trigger}
          />
        )}
      </form>
    </CustomDialog>
  );
};

export const ServicePopup: React.FC<ServicePopupProps> = (
  props: ServicePopupProps,
) => {
  const { serviceUrl, serviceName, serviceDescription, serviceType } = props;
  return (
    <ReactHookFormProvider
      options={{
        ...defaultFormOptions,
        defaultValues: {
          serviceUrl,
          serviceName,
          serviceDescription,
          serviceType,
        },
      }}
    >
      <ServicePopupUnwrapped {...props} />
    </ReactHookFormProvider>
  );
};
